import { FSA } from 'flux-standard-action/lib';
import { EventEmitter } from 'fbemitter';
import { dispatcher } from "../dispatcher";

export type Transform<T, K extends keyof T> = {
    [P in K]: (v: T[P], ...params: any[]) => T[P]
};

// Base class for store in Flux architecture
export class Store<S> extends EventEmitter
{
    protected state: S;
    private handlers: Map<string, Function>;
    readonly dispatchToken: string;

    constructor()
    {
        super();

        this.handlers = new Map();

        this.dispatchToken = dispatcher.register(e => {
            const handler = this.handlers.get(<string>e.type);
            if(handler) {
                const value = handler(e);
                
                // null is a valid value for state property
                if(typeof value === 'object') {
                    this.state = value;
                    this.emit('change');
                }
            }
        });
    }

    // Registers a listener for a particular action
    protected register = <P>(key: string, handler: (fsa: FSA<P, any>) => void | S) => {
        this.handlers.set(key, handler);
    }

    // Iterates over each key in a map and applies the transformation to the state object
    // Example:
    // state = { name: 'Rustam' }
    // map = { name: s => s + '!' }
    // result = { name: 'Rustam!' }
    protected getUpdate = <K extends keyof S>(map: Transform<S, K>, ...params: any[]): Pick<S, K> => {
        const result = {} as Pick<S, K>;
        for(let k in map)
            result[k] = map[k](this.state[k], ...params);
        return result;
    };

    // Every store has a state property
    // This method helps to update fields/properties in that state
    protected field = <K extends keyof S>(pair: Pick<S, K>) => {
        return Object.assign({}, this.state, pair);
    }

    getState = (): S => {
        const state: any = this.state;
        return { ...state };
    }
}


// WEBPACK FOOTER //
// ./src/flux/Store.ts